import { useEffect, useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import PolicyPauseDayInfoView from '@/dashboard/components/Products/policyCard/dialogs/pausePolicy/policyPauseDayInfo';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { DatePickerControlled } from '@/dashboard/mui/form/DatePicker';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { ShieldCheckIcon } from '@heroicons/react/outline';
import { apiExtendPolicy, convertDayjsTimeToServerStoredTimeData, judgeWhetherIsLaterThanTheDate } from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';

type ExtendPolicyFormViewProps = CustomDialogProps & {
  policy: PolicyModel;
  confirm?: (info: any) => void;
  switchToPolicy: () => void;
  submitInfo: {
    endDate: Dayjs;
  };
};

const ExtendPolicyFormView = (props: ExtendPolicyFormViewProps) => {
  const { policy, close, confirm, switchToPolicy, submitInfo: { endDate } = {} } = props;
  const [loading, setLoading] = useState(false);
  const methods = useForm({
    defaultValues: { endDate: endDate || policy.policyEndDateDayjs },
  });
  const { register, getValues, setValue, handleSubmit, control } = methods;
  const [showTimeAlert, setShowTimeAlert] = useState(false);
  const [{ fetchPolicies }] = usePolicyStore(({ actions }) => [actions]);
  const [policyDaysInfo] = useState(policy['overview']);

  /**
   * request
   */
  const requestExtendPolicy = async () => {
    const params = {
      policy_id: policy.id,
      new_end_date: convertDayjsTimeToServerStoredTimeData(getValues('endDate')),
    };

    setLoading(true);
    const [res, err] = await apiExtendPolicy(params);
    setLoading(false);
    if (res) {
      fetchPolicies();
      if (confirm) {
        confirm({ endDate: getValues('endDate') });
      }
    }
  };

  /**
   * mounted
   */
  useEffect(() => {
    endDateChangeAction(getValues('endDate'));
  }, []);

  /**
   * methods
   */
  const closeAction = () => {
    if (close) close();
  };

  const extendAction = () => {
    handleSubmit(requestExtendPolicy)();
  };

  // end date change
  const endDateChangeAction = (newValue) => {
    // judge select time is one year later
    const oneYearLater = policy.policyEndDateDayjs.add(1, 'year');
    const selectDate = dayjs(newValue);
    setShowTimeAlert(selectDate.isSame(oneYearLater) || selectDate.isAfter(oneYearLater));
    setValue('endDate', selectDate);
  };

  const switchToPolicyAction = () => {
    if (switchToPolicy) {
      switchToPolicy();
      closeAction();
    }
  };

  // end time validate
  const endDateValidate = (time) => {
    return judgeWhetherIsLaterThanTheDate(time, policy.policyEndDateDayjs) ? true : 'Please select a valid date';
  };

  return (
    <FormProvider {...methods}>
      <Box className="relative flex flex-col items-center">
        {loading && <LogoLoading />}
        <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
          <ShieldCheckIcon className="h-7 w-7" />
        </Box>
        <Typography className="text-black-secondary mt-4 font-black" variant="h5">
          Extend policy
        </Typography>
        <PolicyPauseDayInfoView className="mt-7" info={policyDaysInfo} />
        <Typography className="text-black-secondary mt-4 font-bold" variant="subtitle1">
          What is your contract end date?
        </Typography>
        <DatePickerControlled
          className="mt-5 w-full"
          name="endDate"
          control={control}
          {...register('endDate', { required: true, validate: endDateValidate })}
          minDate={policy.policyEndDateDayjs}
          label="End date"
          onDateChange={(value) => endDateChangeAction(value)}
        />
        {showTimeAlert && (
          <Typography className="mt-3" variant="body2">
            Contracts can only be covered up to 12 months. You can continue quoting for
            <Button className="mx-1 p-0" variant="text" onClick={switchToPolicyAction}>
              annual policy
            </Button>
            alternatively.
          </Typography>
        )}
        <Box className="mt-6 flex w-full flex-row justify-end">
          <Button className="text-brand-primary text-lg font-bold" variant="text" onClick={closeAction}>
            Cancel
          </Button>
          <LoadingButton
            disabled={showTimeAlert}
            className="ml-2 h-12 w-[174px] rounded-full text-lg font-bold"
            loadingIndicator="Loading..."
            color="primary"
            onClick={extendAction}
            loading={loading}
            variant="contained"
          >
            <span>Extend policy</span>
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default ExtendPolicyFormView;
